<template>
  <v-card>
    <v-card-title>
      <span>Integraciones Configuradas</span>
      <v-spacer />
      <v-text-field
        v-model="companySearch"
        append-icon="mdi-magnify"
        label="Buscar"
        single-line
        hide-details
        style="max-width: 400px"
      />
      <v-spacer />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredCompanies"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:[`item.integrations`]="{ item }">
        <v-chip
          v-for="integration in item.integrations"
          :key="integration"
          small
          class="mr-1"
        >
          {{ integration }}
        </v-chip>
        <span v-if="!item.integrations.length">Sin integraciones</span>
      </template>

      <template v-slot:[`item.terminales`]="{ item }">
        <div class="d-flex justify-center">
          <v-btn
            icon
            color="primary"
            @click="openTerminals(item)"
          >
            <v-icon>mdi-desktop-classic</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.agregar_integracion`]="{ item }">
        <v-btn
          small
          outlined
          color="secondary"
          @click="openIntegrationForm(item)"
        >
          <v-icon left small>mdi-desktop-plus</v-icon>
          Registrar integración TEF
        </v-btn>
      </template>

      <template v-slot:[`item.reporte`]="{ item }">
        <div class="d-flex justify-center">
          <v-btn
            small
            outlined
            color="info"
            @click="openReportForm(item)"
          >
            <v-icon small>mdi-file-chart</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialogOpen"
      max-width="1100"
    >
      <v-card v-if="selectedCompany">
        <v-card-title>
          Terminales - {{ selectedCompany.companyName }}
          <v-spacer />
          <v-btn
            icon
            @click="dialogOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-select
            v-model="selectedEstablishment"
            :items="establishmentOptions"
            label="Selecciona un establecimiento"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-store"
            class="mb-4"
          />

          <v-alert
            v-if="!selectedEstablishment"
            type="info"
            text
          >
            Selecciona un establecimiento para consultar sus terminales.
          </v-alert>

          <v-data-table
            v-if="selectedEstablishment"
            :headers="terminalHeaders"
            :items="visibleTerminals"
            class="elevation-1"
          >
            <template v-slot:[`item.token_key`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    style="cursor: pointer"
                    v-bind="attrs"
                    v-on="on"
                    @click="copyToken(item.token_key)"
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>Copiar Token </span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.integrations`]="{ item }">
              <v-chip
                v-for="integration in item.integrations"
                :key="integration.id"
                small
                class="mr-1 mt-1 mb-1"
              >
                {{ integration.name_tef }}
              </v-chip>
              <span v-if="!item.integrations.length">
                Sin integraciones
              </span>
            </template>

            <template v-slot:[`item.acciones`]="{ item }">
              <div v-if="item.integrations.length === 1" class="d-flex">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openCredentialEditor(item, item.integrations[0])"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar configuración</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="orange darken-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="openTimeoutEditor(item, item.integrations[0])"
                    >
                      <v-icon small>mdi-clock-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar tiempos</span>
                </v-tooltip>
              </div>
              <v-menu v-else-if="item.integrations.length > 1" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left small>mdi-cog-outline</v-icon>
                    Gestionar
                  </v-btn>
                </template>
                <v-list dense>
                  <v-subheader>Integraciones</v-subheader>
                  <v-list-item
                    v-for="integration in item.integrations"
                    :key="`actions-${integration.id}`"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ integration.name_tef }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="d-flex">
                        <v-btn
                          icon
                          small
                          title="Editar configuración"
                          @click="openCredentialEditor(item, integration)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          color="orange darken-2"
                          title="Editar tiempos"
                          @click="openTimeoutEditor(item, integration)"
                        >
                          <v-icon small>mdi-clock-edit-outline</v-icon>
                        </v-btn>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:[`item.agregar_integracion`]="{ item }">
              <v-btn
                small
                outlined
                color="primary"
                @click="openIntegrationForm(selectedCompany, item)"
              >
                <v-icon left small>mdi-plus</v-icon>
                Agregar integración
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-dialog
          v-model="credentialDialogOpen"
          max-width="1000"
          scrollable
        >
          <v-card>
            <v-toolbar
              dark
              color="red darken-2"
            >
              Datos de integración TEF - {{ selectedCompany.companyName }}
              <v-spacer />
              <v-btn icon @click="credentialDialogOpen = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <v-progress-linear
                v-if="credentialLoading"
                indeterminate
                color="primary"
              />
              <v-alert v-if="credentialError" type="error" text>
                {{ credentialError }}
              </v-alert>
              <campo-smart-pos
                v-if="selectedIntegrationData && [1, 3, 7].includes(Number(selectedIntegrationData.type_tef))"
                :data-tef="selectedIntegrationData.data_integration"
                :token_key="selectedTerminal.token_key"
                :code_store="selectedTerminal.code_store"
                :type_tef="selectedIntegrationData.type_tef"
              />
              <campo-credibanco-apps
                v-if="selectedIntegrationData && Number(selectedIntegrationData.type_tef) === 2"
                :data-tef="selectedIntegrationData.data_integration"
                :token_key="selectedTerminal.token_key"
                :code_store="selectedTerminal.code_store"
                :type_tef="selectedIntegrationData.type_tef"
              />
              <campo-credibanco-tramas
                v-if="selectedIntegrationData && Number(selectedIntegrationData.type_tef) === 4"
                :data-tef="selectedIntegrationData.data_integration"
                :token_key="selectedTerminal.token_key"
                :code_store="selectedTerminal.code_store"
                :type_tef="selectedIntegrationData.type_tef"
              />
              <campo-redeban-apps
                v-if="selectedIntegrationData && Number(selectedIntegrationData.type_tef) === 6"
                :data-tef="selectedIntegrationData.data_integration"
                :token_key="selectedTerminal.token_key"
                :code_store="selectedTerminal.code_store"
                :type_tef="selectedIntegrationData.type_tef"
              />
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-dialog
          v-model="timeoutDialogOpen"
          max-width="600"
        >
          <v-card>
            <v-toolbar dark color="red darken-2">
              Tiempos de consulta TEF - {{ selectedCompany.companyName }}
              <v-spacer />
              <v-btn icon @click="timeoutDialogOpen = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <v-progress-linear
                v-if="credentialLoading"
                indeterminate
                color="primary"
              />
              <v-alert v-if="credentialError" type="error" text>
                {{ credentialError }}
              </v-alert>
              <v-alert v-if="timeoutSuccess" type="success" text>
                {{ timeoutSuccess }}
              </v-alert>
              <v-row v-if="!credentialLoading && !credentialError">
                <v-col cols="12" sm="6">
                  <v-subheader class="pl-0">
                    Tiempo de espera transacción
                  </v-subheader>
                  <v-slider
                    v-model="timeoutData.time_out_transaction"
                    :thumb-size="24"
                    min="70"
                    max="180"
                    color="red"
                    thumb-label="always"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-subheader class="pl-0">
                    Tiempo secuencial de consulta
                  </v-subheader>
                  <v-slider
                    v-model="timeoutData.time_out_sequential"
                    :thumb-size="24"
                    min="4"
                    max="20"
                    color="red"
                    thumb-label="always"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="justify-end">
              <v-btn
                color="red darken-2"
                :loading="timeoutSaving"
                @click="updateTimeout"
              >
                Actualizar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="integrationDialogOpen"
      max-width="900"
    >
      <add-integration-bulk-tef
        v-if="selectedCompany"
        :key="`${selectedCompany.IdCompania}-${selectedTerminal ? selectedTerminal.IdTerminal : 'new'}`"
        :company="selectedCompany"
        :terminal="selectedTerminal"
        :creating-terminal="!selectedTerminal"
        @close="integrationDialogOpen = false"
        @saved="onIntegrationSaved"
      />
    </v-dialog>

    <v-dialog v-model="reportDialogOpen" max-width="500">
      <v-card v-if="selectedCompany">
        <v-card-title>Reporte Transacciones </v-card-title>
        <v-card-text>
          <p>{{ selectedCompany.companyName }}</p>
          <v-select
            v-model="reportOptions.establishmentName"
            :items="establishmentOptions"
            label="Establecimiento (opcional)"
            clearable
            outlined
            dense
            prepend-inner-icon="mdi-store"
          />
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="reportOptions.dateFrom"
                type="date"
                label="Fecha inicial"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="reportOptions.dateTo"
                type="date"
                label="Fecha final"
              />
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col cols="6">
              <v-checkbox
                v-model="reportOptions.includeXml"
                label="Incluir XML"
                class="mt-0"
              />
            </v-col>
            <v-col cols="6">
              <v-checkbox
                v-model="reportOptions.includeTrama"
                label="Incluir trama"
                class="mt-0"
              />
            </v-col>
          </v-row>
          <v-alert v-if="reportMessage" type="error" text>
            {{ reportMessage }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="reportDialogOpen = false">
            Cancelar
          </v-btn>
          <v-btn
            dark
            style="background-color: rgb(204 33 40); color: white !important;"
            :loading="reportLoading"
            @click="downloadReport"
          >
            Descargar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios'
import { environment } from '@/data/environment/'

export default {
  name: 'ShowTerminalsTef',

  components: {
    AddIntegrationBulkTef: () => import('./AddIntegrationBulkTef'),
    CampoSmartPos: () => import('./Credibanco/CampoSmartPos'),
    CampoCredibancoApps: () => import('./Credibanco/CampoCredibancoApps'),
    CampoCredibancoTramas: () => import('./Credibanco/CampoCredibancoTramas'),
    CampoRedebanApps: () => import('./Redeban/CampoRedebanApps')
  },

  data () {
    return {
      selectedCompany: null,
      selectedTerminal: null,
      selectedEstablishment: null,
      companySearch: '',
      credentialDialogOpen: false,
      timeoutDialogOpen: false,
      credentialLoading: false,
      credentialError: '',
      selectedIntegration: null,
      selectedIntegrationData: null,
      timeoutSaving: false,
      timeoutSuccess: '',
      timeoutData: {
        token_key: '',
        code_store: '',
        type_tef: null,
        time_out_transaction: 70,
        time_out_sequential: 4
      },
      dialogOpen: false,
      integrationDialogOpen: false,
      reportDialogOpen: false,
      reportLoading: false,
      reportMessage: '',
      reportOptions: {
        includeXml: false,
        includeTrama: false,
        establishmentName: null,
        dateFrom: '',
        dateTo: ''
      },
      loading: false,
      headers: [
        { text: 'Compañía', value: 'companyName' },
        { text: 'Integraciones', value: 'integrations', sortable: false },
        { text: 'Registrar integración TEF', value: 'agregar_integracion', sortable: false },
        {
          text: 'Terminales',
          value: 'terminales',
          sortable: false,
          align: 'center'
        },
        {
          text: 'Reporte Transacciones',
          value: 'reporte',
          sortable: false,
          align: 'center'
        }
      ],
      terminalHeaders: [
        { text: 'Código terminal HIOPOS', value: 'code_store' },
        { text: 'Token key', value: 'token_key', sortable: false },
        { text: 'Integraciones', value: 'integrations', sortable: false },
        { text: 'Gestionar', value: 'acciones', sortable: false },
        { text: 'Agregar integración', value: 'agregar_integracion', sortable: false }
      ]
    }
  },

  computed: {
    establishmentOptions () {
      if (!this.selectedCompany) {
        return []
      }

      return [...new Set(
        this.selectedCompany.terminals.map(terminal => {
          const name = terminal.establishment_name
            ? String(terminal.establishment_name).trim()
            : ''
          return name || 'Sin establecimiento'
        })
      )].sort((first, second) => first.localeCompare(second))
    },

    visibleTerminals () {
      if (!this.selectedCompany || !this.selectedEstablishment) {
        return []
      }

      return this.selectedCompany.terminals.filter(terminal => {
        const name = terminal.establishment_name
          ? String(terminal.establishment_name).trim()
          : ''
        return (name || 'Sin establecimiento') === this.selectedEstablishment
      })
    },

    clients () {
      return (
        this.$store.state.invoice.listClients.Mensaje || []
      )
        .filter(client => client.Activo === 1)
        .map(client => ({
          ...client,
          text: `[${client.IdCompania}] ${client.NombreCompania}`
        }))
    },

    terminals () {
      return this.$store.state.credentialsTef.listTerminals
    },

    filteredCompanies () {
      const search = this.companySearch.trim().toLocaleLowerCase()

      if (!search) {
        return this.companies
      }

      return this.companies.filter(company =>
        String(company.companyName).toLocaleLowerCase().includes(search)
      )
    },

    companies () {
      const groups = {}

      this.terminals.forEach(terminal => {
        const company = this.clients.find(
          item => Number(item.IdCompania) === Number(terminal.IdCompania)
        )
        const key = terminal.IdCompania

        if (!groups[key]) {
          groups[key] = {
            IdCompania: terminal.IdCompania,
            companyName: company
              ? company.text
              : `[${terminal.IdCompania}] Compañía no encontrada`,
            integrations: [],
            terminals: [],
            terminales: 0
          }
        }

        const establishmentName = terminal.establishment_name
          ? String(terminal.establishment_name).trim()
          : ''
        const terminalMapped = {
          ...terminal,
          establishment_name: establishmentName
        }

        groups[key].terminals.push(terminalMapped)
        groups[key].terminales += 1

        terminal.integrations.forEach(integration => {
          if (!groups[key].integrations.includes(integration.name_tef)) {
            groups[key].integrations.push(integration.name_tef)
          }
        })
      })

      return Object.keys(groups).map(key => groups[key])
    }
  },

  async created () {
    await this.$store.dispatch('invoice/getListClients')
    await this.loadTerminals()
  },

  methods: {
    openTerminals (company) {
      this.selectedCompany = company
      this.selectedEstablishment = this.getDefaultEstablishment(company)
      this.dialogOpen = true
    },

    openIntegrationForm (company, terminal) {
      this.selectedCompany = company
      this.selectedTerminal = terminal
      this.integrationDialogOpen = true
    },

    async openCredentialEditor (terminal, integration) {
      this.selectedTerminal = terminal
      this.selectedIntegration = integration
      this.selectedIntegrationData = null
      this.credentialError = ''
      this.credentialLoading = true
      this.credentialDialogOpen = true

      try {
        const detail = await this.loadIntegrationDetail(terminal, integration)
        this.selectedIntegrationData = {
          ...detail,
          data_integration: this.parseIntegrationData(detail.data_integration)
        }
      } catch (error) {
        this.credentialError = 'No fue posible cargar los datos de la integración.'
      } finally {
        this.credentialLoading = false
      }
    },

    async openTimeoutEditor (terminal, integration) {
      this.selectedTerminal = terminal
      this.selectedIntegration = integration
      this.credentialError = ''
      this.credentialLoading = true
      this.timeoutDialogOpen = true

      try {
        const detail = await this.loadIntegrationDetail(terminal, integration)
        this.timeoutData = {
          token_key: terminal.token_key,
          code_store: terminal.code_store,
          type_tef: detail.type_tef,
          time_out_transaction: Number(detail.time_out_transaction),
          time_out_sequential: Number(detail.time_out_sequential)
        }

        if (
          !Number.isInteger(this.timeoutData.time_out_transaction) ||
          !Number.isInteger(this.timeoutData.time_out_sequential)
        ) {
          throw new Error('La configuración no tiene tiempos válidos.')
        }
      } catch (error) {
        this.credentialError = 'No fue posible cargar los tiempos de consulta.'
      } finally {
        this.credentialLoading = false
      }
    },

    async loadIntegrationDetail (terminal, integration) {
      const response = await this.$store.dispatch(
        'credentialsTef/getTerminalIntegrations',
        {
          token_key: terminal.token_key,
          code_store: terminal.code_store
        }
      )
      const terminalData = response.data.respuesta.Datos[0]
      const detail = terminalData.integrations.find(
        item => Number(item.id) === Number(integration.id)
      )

      if (!detail) {
        throw new Error('No se encontró la integración seleccionada.')
      }

      return detail
    },

    parseIntegrationData (data) {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data

      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Los datos de integración no tienen un formato válido.')
      }

      return parsed
    },

    async updateTimeout () {
      this.timeoutSaving = true
      this.credentialError = ''
      this.timeoutSuccess = ''

      try {
        await this.$store.dispatch(
          'credentialsTef/updateCredentialsTimeOut',
          {
            ...this.timeoutData,
            user: JSON.parse(atob(localStorage.getItem('uid'))).user,
            observation: 'Updated Timeouts'
          }
        )
        this.timeoutSuccess = 'Tiempos actualizados exitosamente.'
      } catch (error) {
        this.credentialError = 'No fue posible actualizar los tiempos de consulta.'
      } finally {
        this.timeoutSaving = false
      }
    },

    openReportForm (company) {
      this.selectedCompany = company
      this.reportOptions = {
        includeXml: false,
        includeTrama: false,
        establishmentName: null,
        dateFrom: '',
        dateTo: ''
      }
      this.reportMessage = ''
      this.reportDialogOpen = true
    },

    async downloadReport () {
      this.reportLoading = true
      this.reportMessage = ''

      try {
        const response = await axios
          .create()
          .get(
            `${environment.HIOPOSWEBSERVICES}/api/terminals/report/${this.selectedCompany.IdCompania}`,
            {
              params: {
                include_xml: this.reportOptions.includeXml ? 1 : 0,
                include_trama: this.reportOptions.includeTrama ? 1 : 0,
                establishment_name: this.reportOptions.establishmentName || undefined,
                date_from: this.reportOptions.dateFrom,
                date_to: this.reportOptions.dateTo
              },
              responseType: 'blob'
            }
          )
        const blobUrl = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `reporte_tef_${this.selectedCompany.IdCompania}.xlsx`
        link.click()
        window.URL.revokeObjectURL(blobUrl)
        this.reportDialogOpen = false
      } catch (error) {
        this.reportMessage = 'No fue posible generar el reporte TEF.'
      } finally {
        this.reportLoading = false
      }
    },

    async onIntegrationSaved () {
      await this.loadTerminals()
      this.integrationDialogOpen = false
    },

    async copyToken (token) {
      if (!token) {
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(String(token))
        } else {
          const input = document.createElement('textarea')
          input.value = String(token)
          input.setAttribute('readonly', '')
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.focus()
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$emit('token-copied')
      } catch (error) {
        this.$emit('token-copy-error')
      }
    },

    async loadTerminals () {
      this.loading = true
      try {
        await this.$store.dispatch(
          'credentialsTef/getTerminals',
          null
        )
      } finally {
        this.loading = false
      }
    },

    getDefaultEstablishment (company) {
      const establishments = [...new Set(
        company.terminals.map(terminal => {
          const name = terminal.establishment_name
            ? String(terminal.establishment_name).trim()
            : ''
          return name || 'Sin establecimiento'
        })
      )]

      return establishments.length === 1 ? establishments[0] : null
    }
  }
}
</script>
